import type {
  Editor,
  NodeViewProps,
  ResizableNodeViewDirection,
} from "@tiptap/core";
import { ResizableNodeView } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";

/** Match TipTap Image: side bars only (width resize, aspect ratio preserved). */
export const RESIZE_DIRECTIONS: ResizableNodeViewDirection[] = [
  "left",
  "right",
];

export const RESIZE_CLASS_NAMES = {
  container: "richtext-resize-container",
  wrapper: "richtext-resize-wrapper",
  handle: "richtext-resize-handle",
  resizing: "is-resizing",
} as const;

export type ResizableMediaOptions = {
  enabled: boolean;
  minWidth?: number;
  minHeight?: number;
  preserveAspectRatio?: boolean;
};

type CreateResizableNodeViewArgs = {
  element: HTMLElement;
  node: ProseMirrorNode;
  editor: Editor;
  getPos: NodeViewProps["getPos"];
  nodeName: string;
  minWidth: number;
  minHeight: number;
  preserveAspectRatio?: boolean;
  directions?: ResizableNodeViewDirection[];
  onUpdate?: (updatedNode: ProseMirrorNode) => boolean;
  onDestroy?: () => void;
};

/**
 * TipTap's default handles sit flush on the outer edge and get clipped by our
 * `overflow: hidden` editor. Build inset handles that match Image's left/right
 * bars (full-height) and optional corner/edge handles.
 */
export const createInsetResizeHandle = (
  direction: ResizableNodeViewDirection,
) => {
  const handle = document.createElement("div");
  handle.dataset.resizeHandle = direction;
  handle.className = RESIZE_CLASS_NAMES.handle;
  handle.style.position = "absolute";
  handle.style.zIndex = "10";
  handle.style.boxSizing = "border-box";
  handle.style.touchAction = "none";
  handle.style.userSelect = "none";
  handle.style.setProperty("-webkit-user-select", "none");

  const inset = "4px";
  const edgeInset = "8px";

  // Full-height side bars — same idea as TipTap Image's left/right handles.
  if (direction === "left" || direction === "right") {
    handle.style.width = "6px";
    handle.style.top = edgeInset;
    handle.style.bottom = edgeInset;
    handle.style.cursor = "ew-resize";
    if (direction === "left") {
      handle.style.left = inset;
    } else {
      handle.style.right = inset;
    }
    return handle;
  }

  if (direction === "top" || direction === "bottom") {
    handle.style.height = "6px";
    handle.style.left = edgeInset;
    handle.style.right = edgeInset;
    handle.style.cursor = "ns-resize";
    if (direction === "top") {
      handle.style.top = inset;
    } else {
      handle.style.bottom = inset;
    }
    return handle;
  }

  handle.style.width = "10px";
  handle.style.height = "10px";
  if (direction.includes("top")) {
    handle.style.top = inset;
  }
  if (direction.includes("bottom")) {
    handle.style.bottom = inset;
  }
  if (direction.includes("left")) {
    handle.style.left = inset;
  }
  if (direction.includes("right")) {
    handle.style.right = inset;
  }
  if (direction === "top-left" || direction === "bottom-right") {
    handle.style.cursor = "nwse-resize";
  } else if (direction === "top-right" || direction === "bottom-left") {
    handle.style.cursor = "nesw-resize";
  }

  return handle;
};

const persistNodeSize = (
  editor: Editor,
  getPos: NodeViewProps["getPos"],
  nodeName: string,
  width: number,
  height: number,
) => {
  const pos = getPos();
  if (typeof pos !== "number") {
    return;
  }

  // Prefer a direct markup update over setNodeSelection + updateAttributes.
  // Changing selection during mouseup is a common source of "sticky" resizes
  // (especially with iframe/video, which can also steal pointer events).
  const { state, dispatch } = editor.view;
  const current = state.doc.nodeAt(pos);
  if (!current || current.type.name !== nodeName) {
    return;
  }

  dispatch(
    state.tr.setNodeMarkup(pos, undefined, {
      ...current.attrs,
      width: Math.round(width),
      height: Math.round(height),
    }),
  );
};

/**
 * TipTap's ResizableNodeView misses touchmove cleanup on mouseup and can leave
 * listeners around when an iframe steals the pointer. Mirror the built-in end
 * path and also listen for pointerup / touchend / blur as a safety net.
 */
const patchResizeEndHandling = (nodeView: ResizableNodeView) => {
  // ResizableNodeView keeps these as private fields; we patch them carefully.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const view = nodeView as any;

  const endResize = () => {
    if (!view.isResizing) {
      return;
    }

    const finalWidth = view.element.offsetWidth as number;
    const finalHeight = view.element.offsetHeight as number;
    view.onCommit(finalWidth, finalHeight);

    view.isResizing = false;
    view.activeHandle = null;
    view.container.dataset.resizeState = "false";
    if (view.classNames.resizing) {
      view.container.classList.remove(view.classNames.resizing);
    }

    document.removeEventListener("mousemove", view.handleMouseMove);
    document.removeEventListener("mouseup", view.handleMouseUp);
    document.removeEventListener("touchmove", view.handleTouchMove);
    document.removeEventListener("touchend", endResize);
    document.removeEventListener("pointerup", endResize);
    window.removeEventListener("blur", endResize);
    document.removeEventListener("keydown", view.handleKeyDown);
    document.removeEventListener("keyup", view.handleKeyUp);
  };

  view.handleMouseUp = endResize;

  view.handleTouchMove = (event: TouchEvent) => {
    if (!view.isResizing || !view.activeHandle) {
      return;
    }
    // Non-passive listener (registered below) so this can stop webview scroll.
    event.preventDefault();
    const touch = event.touches[0] as Touch | undefined;
    if (!touch) {
      return;
    }
    view.handleResize(
      touch.clientX - (view.startX as number),
      touch.clientY - (view.startY as number),
    );
  };

  const originalStart = view.handleResizeStart.bind(view) as (
    event: MouseEvent | TouchEvent,
    direction: ResizableNodeViewDirection,
  ) => void;

  view.handleResizeStart = (
    event: MouseEvent | TouchEvent,
    direction: ResizableNodeViewDirection,
  ) => {
    originalStart(event, direction);
    // TipTap adds a passive touchmove; replace so preventDefault works on mobile.
    document.removeEventListener("touchmove", view.handleTouchMove);
    document.addEventListener("touchmove", view.handleTouchMove, {
      passive: false,
    });
    // Extra end listeners: iframe/video can swallow the document mouseup.
    document.addEventListener("touchend", endResize);
    document.addEventListener("pointerup", endResize);
    window.addEventListener("blur", endResize);
  };
};

/**
 * Wraps a DOM element in TipTap's ResizableNodeView and persists width/height attrs on commit.
 */
export const createResizableNodeView = ({
  element,
  node,
  editor,
  getPos,
  nodeName,
  minWidth,
  minHeight,
  preserveAspectRatio = true,
  directions = RESIZE_DIRECTIONS,
  onUpdate,
  onDestroy,
}: CreateResizableNodeViewArgs) => {
  const nodeView = new ResizableNodeView({
    element,
    editor,
    node,
    getPos,
    onResize: (width, height) => {
      element.style.width = `${width}px`;
      element.style.height = `${height}px`;
    },
    onCommit: (width, height) => {
      persistNodeSize(editor, getPos, nodeName, width, height);
    },
    onUpdate: (updatedNode) => {
      if (updatedNode.type !== node.type) {
        return false;
      }
      return onUpdate?.(updatedNode) ?? true;
    },
    options: {
      directions: [...directions],
      min: { width: minWidth, height: minHeight },
      preserveAspectRatio,
      className: { ...RESIZE_CLASS_NAMES },
      createCustomHandle: createInsetResizeHandle,
    },
  });

  patchResizeEndHandling(nodeView);

  if (onDestroy) {
    const destroy = nodeView.destroy.bind(nodeView);
    nodeView.destroy = () => {
      onDestroy();
      destroy();
    };
  }

  return nodeView;
};
